import { Loader2, Orbit as OrbitIcon, Pin, Plus, RefreshCw, Waypoints } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ForceGraph2D, { type ForceGraphMethods } from "react-force-graph-2d";
import {
  forceCollide,
  forceLink,
  forceManyBody,
  forceSimulation,
  type Simulation,
  type SimulationLinkDatum,
} from "d3-force";
import { toast } from "sonner";

import { EmptyState, ErrorState, LoadingState } from "@/components/feedback";
import { QuickAddMenu } from "@/components/shared/QuickAddMenu";
import { Button } from "@/components/ui/button";
import { Tip } from "@/components/ui/overlays";
import { keys, useOrbits, useRecomputeOrbits } from "@/hooks/queries";
import { api, queryClient, type NodeType, type OrbitLayout, type OrbitOut } from "@/lib/api";
import { CANVAS_BACKGROUND, CANVAS_FOREGROUND, CANVAS_MUTED, CANVAS_PRIMARY, NODE_COLOR } from "@/lib/palette";
import { cn } from "@/lib/utils";
import { useGraph } from "@/stores/graph";
import { useShell } from "@/stores/shell";
import { useTabs } from "@/stores/tabs";
import { useIsCompact } from "@/hooks/use-media-query";

/** The association-based orbit graphing system.
 *
 * The server computes whole "systems": each ORG/hub node is a CENTER; the nodes
 * connected to it sit on rings AROUND it, and the connecting edges are still
 * drawn (the orbit organizes the edges spatially, it does not replace them).
 * Classification gives four orbit kinds:
 *   - satellite — thin concentric rings of independent neighbours
 *   - planetary — a wider belt with ONE anchor pinned on the ring and the rest
 *     ("free" role) force-simulated locally around it, kept on the belt band
 *   - polygon   — an N-gon of mutually-connected nodes (no implied center)
 *   - free      — leftover concentric rings around the global origin
 *
 * The server is the source of truth for geometry. For ring/anchor/vertex/center
 * roles `theta` is authoritative and x/y are derived on the ring (we pin them).
 * For `free` roles x/y are authoritative — those nodes run a LOCAL d3 force
 * simulation here and we PATCH their settled positions back.
 *
 * Canvas convention matches `graphs/orbits.position()`:
 *   x = cx + r·cos θ,  y = cy + r·sin θ   (+y is screen-down). */

type ORole = "ring" | "anchor" | "free" | "vertex" | "center";

type ONode = {
  id: string;
  orbitId: string;
  centerId: string | null;
  role: ORole;
  label: string;
  type: NodeType;
  kind: OrbitOut["kind"];
  cx: number;
  cy: number;
  r: number;
  theta: number;
  beltWidth: number;
  x?: number;
  y?: number;
  fx?: number;
  fy?: number;
  vx?: number;
  vy?: number;
  index?: number;
};

type OLink = { id: string; a: ONode; b: ONode };
type SimLink = SimulationLinkDatum<ONode> & { source: string; target: string };

const TAU = Math.PI * 2;
const NODE_RADIUS = 6;
const MIN_ARC = 42; // node diameter + label padding — short link distance for the local sim

// Studio palette (obsidian — mirrors the atlas canvas, see lib/palette.ts).
const RING_STROKE = `rgba(${CANVAS_MUTED}, 0.45)`;
const BELT_FILL = `rgba(${CANVAS_PRIMARY}, 0.06)`;
const BELT_EDGE = `rgba(${CANVAS_MUTED}, 0.25)`;
const EDGE_STROKE = `rgba(${CANVAS_MUTED}, 0.5)`;
const POLY_STROKE = `rgba(${CANVAS_MUTED}, 0.3)`;

function useBox<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      if (!entry) return;
      const { width, height } = entry.contentRect;
      setSize({ width: Math.floor(width), height: Math.floor(height) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return { ref, size };
}

/** A node's live position, tolerant of the pin (fx/fy) vs sim (x/y) sources. */
function px(n: ONode): number {
  return n.x ?? n.fx ?? n.cx;
}
function py(n: ONode): number {
  return n.y ?? n.fy ?? n.cy;
}

/** Snap a ring/anchor/vertex node back onto its ring, recording the angle. The
 * ONLY motion those roles are allowed — radius and center stay fixed. */
function snapToRing(n: ONode): void {
  const ang = Math.atan2(py(n) - n.cy, px(n) - n.cx);
  n.theta = (ang + TAU) % TAU;
  const x = n.cx + n.r * Math.cos(ang);
  const y = n.cy + n.r * Math.sin(ang);
  n.x = x;
  n.y = y;
  n.fx = x;
  n.fy = y;
}

/** Stable small hash for an edge id → a deterministic ± bow direction so that
 * edges sharing an endpoint don't stack on top of each other. */
function hashSign(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return h % 2 === 0 ? 1 : -1;
}

/** Custom d3 force: pull every `free` node toward its OWN ring centerline
 * (each free node carries its orbit's cx/cy/r), so the cluster hugs the belt. */
function makeBandForce() {
  let free: ONode[] = [];
  const force = (alpha: number) => {
    for (const n of free) {
      const dx = (n.x ?? n.cx) - n.cx;
      const dy = (n.y ?? n.cy) - n.cy;
      const dist = Math.hypot(dx, dy) || 1e-6;
      const k = (0.7 * alpha * (n.r - dist)) / dist;
      n.vx = (n.vx ?? 0) + dx * k;
      n.vy = (n.vy ?? 0) + dy * k;
    }
  };
  force.initialize = (nodes: ONode[]) => {
    free = nodes.filter((n) => n.role === "free");
  };
  return force;
}

export function OrbitTab({
  visible,
  onAddWebsite,
}: {
  visible: boolean;
  onAddWebsite: () => void;
}) {
  const orbits = useOrbits();
  const recompute = useRecomputeOrbits();
  const compact = useIsCompact();
  // Two-click on empty space opens the same "add to the atlas" menu as the
  // atlas — so a file/note can be added straight from the orbits view.
  const [quickAdd, setQuickAdd] = useState<{ x: number; y: number } | null>(null);
  const lastBgClick = useRef<{ t: number; x: number; y: number } | null>(null);
  const fgRef = useRef<ForceGraphMethods<ONode>>();
  const { ref: boxRef, size } = useBox<HTMLDivElement>();
  const [busy, setBusy] = useState(false);
  const didFit = useRef(false);

  // Mutable, per-render copies of the orbit rings + resolved edges, so a center
  // drag can shift them locally for instant feedback without touching the query
  // cache (the server re-derives the truth and a refetch overwrites these).
  const orbitsRef = useRef<OrbitOut[]>([]);
  const linksRef = useRef<OLink[]>([]);
  const endpointDegRef = useRef<Map<string, number>>(new Map());
  const nodesRef = useRef<ONode[]>([]);
  const simRef = useRef<Simulation<ONode, SimLink> | null>(null);

  const layout: OrbitLayout | undefined = orbits.data;

  const data = useMemo(() => {
    const orbitCopies: OrbitOut[] = (layout?.orbits ?? []).map((o) => ({ ...o }));
    const nodes: ONode[] = [];
    const byId = new Map<string, ONode>();
    const seenCenter = new Set<string>();

    for (const o of orbitCopies) {
      // Synthesize the center node once per distinct center (it has NO entry in
      // `nodes[]`). A center is drawn at the orbit's center_* fields.
      if (o.center_node_id && !seenCenter.has(o.center_node_id)) {
        seenCenter.add(o.center_node_id);
        const c: ONode = {
          id: o.center_node_id,
          orbitId: o.id,
          centerId: o.center_node_id,
          role: "center",
          label: o.center_label ?? "",
          type: (o.center_type ?? "concept") as NodeType,
          kind: o.kind,
          cx: o.center_x,
          cy: o.center_y,
          r: 0,
          theta: 0,
          beltWidth: 0,
          x: o.center_x,
          y: o.center_y,
          fx: o.center_x,
          fy: o.center_y,
        };
        nodes.push(c);
        byId.set(c.id, c);
      }
      for (const n of o.nodes) {
        const free = n.role === "free";
        const node: ONode = {
          id: n.node_id,
          orbitId: o.id,
          centerId: o.center_node_id,
          role: n.role,
          label: n.label,
          type: n.type,
          kind: o.kind,
          cx: o.center_x,
          cy: o.center_y,
          r: o.radius,
          theta: n.theta,
          beltWidth: o.belt_width,
          x: n.x,
          y: n.y,
          // ring/anchor/vertex are pinned to their server position; free nodes
          // float under the local sim (no pin).
          fx: free ? undefined : n.x,
          fy: free ? undefined : n.y,
        };
        nodes.push(node);
        byId.set(node.id, node);
      }
    }

    // Resolve the visible edges to live node objects (a center endpoint uses its
    // center node; a placed endpoint uses its placement node).
    const links: OLink[] = [];
    const deg = new Map<string, number>();
    for (const e of layout?.edges ?? []) {
      const a = byId.get(e.src_id);
      const b = byId.get(e.dst_id);
      if (!a || !b) continue;
      links.push({ id: e.id, a, b });
      deg.set(a.id, (deg.get(a.id) ?? 0) + 1);
      deg.set(b.id, (deg.get(b.id) ?? 0) + 1);
    }

    orbitsRef.current = orbitCopies;
    linksRef.current = links;
    endpointDegRef.current = deg;
    nodesRef.current = nodes;
    return { nodes, links: [] as never[], byId };
  }, [layout]);

  // The host ForceGraph simulation stays OFF — this is a geometric layout.
  useEffect(() => {
    const fg = fgRef.current;
    if (!fg) return;
    fg.d3Force("charge", null);
    fg.d3Force("link", null);
    fg.d3Force("center", null);
  }, [data]);

  // Pause the render loop while the tab is hidden.
  useEffect(() => {
    const fg = fgRef.current;
    if (!fg) return;
    if (visible) fg.resumeAnimation();
    else fg.pauseAnimation();
  }, [visible, size.width]);

  // Persist settled free-node positions (role=free => x/y authoritative). We do
  // NOT invalidate the query here, so this can't loop with a sim rebuild.
  const persistFreeNodes = useCallback(() => {
    for (const n of nodesRef.current) {
      if (n.role !== "free") continue;
      const x = px(n);
      const y = py(n);
      const theta = (Math.atan2(y - n.cy, x - n.cx) + TAU) % TAU;
      void api
        .request("PATCH", `/api/orbits/${n.orbitId}/placements/${n.id}`, { theta, x, y })
        .catch(() => {
          /* best-effort — positions re-settle on next recompute */
        });
    }
  }, []);

  // (Re)build the LOCAL planetary simulation whenever the layout changes. It
  // runs only over `free` nodes (mobile) plus the fixed nodes they link to
  // (anchors/centers/ring nodes pinned via fx/fy), independent of the host.
  useEffect(() => {
    simRef.current?.stop();
    simRef.current = null;

    const nodes = data.nodes;
    const free = nodes.filter((n) => n.role === "free");
    if (free.length === 0) return;

    const freeIds = new Set(free.map((n) => n.id));
    const simNodes = new Set<ONode>(free);
    const simLinks: SimLink[] = [];
    for (const e of layout?.edges ?? []) {
      const a = data.byId.get(e.src_id);
      const b = data.byId.get(e.dst_id);
      if (!a || !b) continue;
      if (freeIds.has(a.id) || freeIds.has(b.id)) {
        simNodes.add(a);
        simNodes.add(b);
        simLinks.push({ source: a.id, target: b.id });
      }
    }
    const simNodeArr = [...simNodes];

    const sim = forceSimulation<ONode>(simNodeArr)
      .force(
        "link",
        forceLink<ONode, SimLink>(simLinks)
          .id((d) => d.id)
          .distance(MIN_ARC)
          .strength(0.3),
      )
      .force("charge", forceManyBody<ONode>().strength(-30))
      .force("collide", forceCollide<ONode>(NODE_RADIUS + 4))
      .force("band", makeBandForce())
      .alpha(0.8)
      .alphaDecay(0.05);

    // Each tick: clamp every free node back into its belt band [r±belt/2].
    sim.on("tick", () => {
      for (const n of free) {
        const dx = (n.x ?? n.cx) - n.cx;
        const dy = (n.y ?? n.cy) - n.cy;
        const dist = Math.hypot(dx, dy) || 1e-6;
        const lo = n.r - n.beltWidth / 2;
        const hi = n.r + n.beltWidth / 2;
        const clamped = Math.min(hi, Math.max(lo, dist));
        if (clamped !== dist) {
          n.x = n.cx + (dx / dist) * clamped;
          n.y = n.cy + (dy / dist) * clamped;
        }
      }
    });
    sim.on("end", persistFreeNodes);
    simRef.current = sim;

    return () => {
      sim.stop();
    };
  }, [data, layout, persistFreeNodes]);

  // Fit the view once the first systems arrive (and again after recompute).
  useEffect(() => {
    if (!didFit.current && data.nodes.length && fgRef.current) {
      didFit.current = true;
      setTimeout(() => fgRef.current?.zoomToFit(500, 110), 200);
    }
  }, [data]);

  const createOrbit = useCallback(async () => {
    setBusy(true);
    try {
      const count = orbitsRef.current.length;
      await api.request("POST", "/api/orbits", {
        label: `Orbit ${count + 1}`,
        center_x: count * 380,
        center_y: 0,
        radius: 150,
        populate: 8,
      });
      didFit.current = false;
      await queryClient.invalidateQueries({ queryKey: keys.orbits });
      toast.success("Orbit created");
    } catch (e) {
      toast.error("Could not create orbit", { description: String(e) });
    } finally {
      setBusy(false);
    }
  }, []);

  const onRecompute = useCallback(() => {
    recompute.mutate(undefined, {
      onSuccess: () => {
        didFit.current = false;
        toast.success("Orbits recomputed");
      },
      onError: (e) => toast.error("Could not recompute orbits", { description: String(e) }),
    });
  }, [recompute]);

  // Persist a ring/anchor/vertex angle change (server re-derives x/y).
  const persistTheta = useCallback((n: ONode) => {
    void api
      .request("PATCH", `/api/orbits/${n.orbitId}/placements/${n.id}`, { theta: n.theta })
      .catch((e) => toast.error("Could not move node", { description: String(e) }))
      .finally(() => void queryClient.invalidateQueries({ queryKey: keys.orbits }));
  }, []);

  // Shift a whole center system locally by (ddx, ddy) for instant drag feedback.
  const moveSystemLocal = useCallback((centerId: string, ddx: number, ddy: number) => {
    for (const o of orbitsRef.current) {
      if (o.center_node_id === centerId) {
        o.center_x += ddx;
        o.center_y += ddy;
      }
    }
    for (const n of nodesRef.current) {
      if (n.centerId !== centerId || n.role === "center") continue;
      n.cx += ddx;
      n.cy += ddy;
      if (n.x != null) n.x += ddx;
      if (n.y != null) n.y += ddy;
      if (n.fx != null) n.fx += ddx;
      if (n.fy != null) n.fy += ddy;
    }
  }, []);

  // Drag bookkeeping for center-system moves (translate is cumulative).
  const dragRef = useRef<{ id: string; lastX: number; lastY: number } | null>(null);

  const onNodeDrag = useCallback(
    (node: ONode, translate: { x: number; y: number }) => {
      const n = node;
      if (n.role === "center" && n.centerId) {
        const prev = dragRef.current;
        const baseX = prev && prev.id === n.id ? prev.lastX : 0;
        const baseY = prev && prev.id === n.id ? prev.lastY : 0;
        moveSystemLocal(n.centerId, translate.x - baseX, translate.y - baseY);
        dragRef.current = { id: n.id, lastX: translate.x, lastY: translate.y };
        return;
      }
      if (n.role === "free") return; // free 2-D drag: force-graph sets fx/fy
      snapToRing(n); // ring/anchor/vertex: angle only
    },
    [moveSystemLocal],
  );

  const onNodeDragEnd = useCallback(
    (node: ONode) => {
      const n = node;
      if (n.role === "center" && n.centerId) {
        const t = dragRef.current;
        dragRef.current = null;
        if (t && t.id === n.id && (t.lastX !== 0 || t.lastY !== 0)) {
          void api
            .request("POST", `/api/orbits/${n.orbitId}/translate`, { dx: t.lastX, dy: t.lastY })
            .catch((e) => toast.error("Could not move system", { description: String(e) }));
        }
        return;
      }
      if (n.role === "free") {
        // Release the pin, re-warm the local sim, then persist on settle.
        n.fx = undefined;
        n.fy = undefined;
        simRef.current?.alpha(0.5).restart();
        return;
      }
      snapToRing(n);
      persistTheta(n);
    },
    [persistTheta],
  );

  // The orbit system (center) the most-recently-clicked node belongs to — the
  // target of "Pin orbit".
  const [pinTarget, setPinTarget] = useState<{ id: string; label: string } | null>(null);
  const [pinning, setPinning] = useState(false);

  // Clicking an orbit node opens the SAME inspector "read" side menu as the
  // atlas: select the node (graph store drives the inspector) and, on compact
  // layouts where the inspector is a drawer, pop it open.
  const onNodeClick = useCallback(
    (node: ONode) => {
      useGraph.getState().toggleSelect({ kind: "node", id: node.id });
      if (compact && useGraph.getState().selection) {
        useShell.getState().openRight("inspect");
      }
      // Remember which orbit (center) this node sits in, so "Pin orbit" pins the
      // whole system. A node with no center (a free node) can't pin a system.
      const center =
        node.role === "center"
          ? node
          : data.nodes.find((n) => n.role === "center" && n.id === node.centerId);
      setPinTarget(center ? { id: center.id, label: center.label } : null);
    },
    [compact, data.nodes],
  );

  const pinOrbit = useCallback(async () => {
    if (!pinTarget) return;
    setPinning(true);
    try {
      await api.request("POST", "/api/workspace/items", {
        kind: "node",
        title: pinTarget.label,
        ref_id: pinTarget.id,
        body: "orbit", // marks this as an orbit pin (grouped + reopens here)
      });
      void queryClient.invalidateQueries({ queryKey: keys.workspace });
      toast.success(`Pinned orbit “${pinTarget.label}”`);
    } catch (e) {
      toast.error("Could not pin orbit", { description: String(e) });
    } finally {
      setPinning(false);
    }
  }, [pinTarget]);

  const isEmpty = orbits.isSuccess && (layout?.orbits?.length ?? 0) === 0;
  const dirty = layout?.dirty ?? false;

  return (
    <div
      ref={boxRef}
      className={cn("relative h-full w-full overflow-hidden bg-background", !visible && "hidden")}
    >
      {size.width > 0 && (
        <ForceGraph2D<ONode>
          ref={fgRef}
          width={size.width}
          height={size.height}
          graphData={data}
          backgroundColor="rgba(0,0,0,0)"
          nodeId="id"
          cooldownTicks={0}
          warmupTicks={0}
          enableNodeDrag
          // Rings, belts, polygon outlines, and the VISIBLE edges — all drawn
          // beneath the nodes each frame.
          onRenderFramePre={(ctx: CanvasRenderingContext2D, scale: number) => {
            ctx.save();

            // 1) Rings / belts / polygon circumscribed circles.
            for (const o of orbitsRef.current) {
              if (o.belt_width > 0) {
                // Planetary belt: a faint violet band between the inner/outer edge.
                const inner = Math.max(0, o.radius - o.belt_width / 2);
                const outer = o.radius + o.belt_width / 2;
                ctx.beginPath();
                ctx.arc(o.center_x, o.center_y, outer, 0, TAU);
                ctx.arc(o.center_x, o.center_y, inner, 0, TAU, true);
                ctx.fillStyle = BELT_FILL;
                ctx.fill("evenodd");
                ctx.lineWidth = 0.8 / scale;
                ctx.strokeStyle = BELT_EDGE;
                ctx.beginPath();
                ctx.arc(o.center_x, o.center_y, inner, 0, TAU);
                ctx.stroke();
                ctx.beginPath();
                ctx.arc(o.center_x, o.center_y, outer, 0, TAU);
                ctx.stroke();
                // Centerline, slightly stronger.
                ctx.beginPath();
                ctx.arc(o.center_x, o.center_y, o.radius, 0, TAU);
                ctx.strokeStyle = RING_STROKE;
                ctx.lineWidth = 1.0 / scale;
                ctx.stroke();
              } else {
                ctx.beginPath();
                ctx.arc(o.center_x, o.center_y, o.radius, 0, TAU);
                ctx.strokeStyle = RING_STROKE;
                ctx.lineWidth = 1.1 / scale;
                ctx.stroke();
              }
            }

            // 1b) Polygon outlines — connect consecutive vertices for legibility.
            for (const o of orbitsRef.current) {
              if (o.kind !== "polygon") continue;
              const verts = o.nodes;
              if (verts.length < 2) continue;
              ctx.beginPath();
              for (let i = 0; i < verts.length; i++) {
                const v = verts[i];
                if (!v) continue;
                const x = o.center_x + o.radius * Math.cos(v.theta);
                const y = o.center_y + o.radius * Math.sin(v.theta);
                if (i === 0) ctx.moveTo(x, y);
                else ctx.lineTo(x, y);
              }
              ctx.closePath();
              ctx.strokeStyle = POLY_STROKE;
              ctx.lineWidth = 0.9 / scale;
              ctx.stroke();
            }

            // 2) Visible edges (node→center connectors AND sibling edges). The
            // orbit organizes the edges spatially; it does not replace them.
            const deg = endpointDegRef.current;
            ctx.strokeStyle = EDGE_STROKE;
            ctx.lineWidth = 0.9 / scale;
            for (const l of linksRef.current) {
              const ax = px(l.a);
              const ay = py(l.a);
              const bx = px(l.b);
              const by = py(l.b);
              const shared = (deg.get(l.a.id) ?? 0) > 1 || (deg.get(l.b.id) ?? 0) > 1;
              ctx.beginPath();
              ctx.moveTo(ax, ay);
              if (shared) {
                // Slight perpendicular bow so overlapping lines don't stack.
                const mx = (ax + bx) / 2;
                const my = (ay + by) / 2;
                const dx = bx - ax;
                const dy = by - ay;
                const len = Math.hypot(dx, dy) || 1e-6;
                const off = (hashSign(l.id) * 6) / scale;
                ctx.quadraticCurveTo(mx + (-dy / len) * off, my + (dx / len) * off, bx, by);
              } else {
                ctx.lineTo(bx, by);
              }
              ctx.stroke();
            }

            ctx.restore();
          }}
          nodeCanvasObject={(node, ctx, scale) => {
            const n = node as ONode;
            const x = px(n);
            const y = py(n);
            const isCenter = n.role === "center";
            const r = isCenter ? 8 : 5;
            ctx.beginPath();
            ctx.arc(x, y, r, 0, TAU);
            ctx.fillStyle = NODE_COLOR[n.type] ?? "#A8A18A";
            ctx.fill();
            ctx.lineWidth = (isCenter ? 1.8 : 1.2) / scale;
            ctx.strokeStyle = `rgba(${CANVAS_BACKGROUND}, 0.95)`;
            ctx.stroke();
            // Anchors read as the belt's fixed seat with a subtle outline ring.
            if (n.role === "anchor") {
              ctx.beginPath();
              ctx.arc(x, y, r + 2.5 / scale, 0, TAU);
              ctx.strokeStyle = `rgba(${CANVAS_PRIMARY}, 0.55)`;
              ctx.lineWidth = 1 / scale;
              ctx.stroke();
            }
            if (isCenter || scale > 0.75) {
              ctx.font = `${(isCenter ? 12 : 11) / scale}px "Inter Variable", system-ui, sans-serif`;
              ctx.fillStyle = isCenter ? `rgba(${CANVAS_FOREGROUND}, 0.96)` : `rgba(${CANVAS_FOREGROUND}, 0.85)`;
              ctx.textAlign = "left";
              ctx.textBaseline = "middle";
              // Ellipsis ONLY when the label was actually cut (mirrors
              // graph/paint.ts) — a bare slice just stops mid-word.
              const label = n.label.length > 28 ? n.label.slice(0, 27) + "…" : n.label;
              ctx.fillText(label, x + r + 3 / scale, y);
            }
          }}
          nodePointerAreaPaint={(node, color, ctx) => {
            const n = node as ONode;
            ctx.beginPath();
            ctx.arc(px(n), py(n), n.role === "center" ? 11 : 9, 0, TAU);
            ctx.fillStyle = color;
            ctx.fill();
          }}
          onNodeClick={(node) => onNodeClick(node as ONode)}
          onBackgroundClick={(event: MouseEvent) => {
            setQuickAdd(null);
            const box = boxRef.current?.getBoundingClientRect();
            const x = event.clientX - (box?.left ?? 0);
            const y = event.clientY - (box?.top ?? 0);
            const last = lastBgClick.current;
            const now = performance.now();
            // Two background clicks close in time + space → open the add menu.
            if (last && now - last.t < 420 && Math.hypot(x - last.x, y - last.y) < 14) {
              lastBgClick.current = null;
              setQuickAdd({ x: Math.min(x, (box?.width ?? x) - 184), y: Math.min(y, (box?.height ?? y) - 170) });
            } else {
              lastBgClick.current = { t: now, x, y };
            }
          }}
          onNodeDrag={(node, translate) => onNodeDrag(node as ONode, translate)}
          onNodeDragEnd={(node) => onNodeDragEnd(node as ONode)}
          minZoom={0.2}
          maxZoom={8}
        />
      )}

      {/* Toolbar */}
      <div className="pointer-events-none absolute inset-x-0 top-0 z-10 flex items-start justify-between gap-2 p-3">
        <div className="pointer-events-auto flex flex-col items-start gap-2">
          <div className="rounded-full border border-border bg-popover/85 px-3 py-1.5 font-mono text-2xs text-muted-foreground backdrop-blur">
            <span className="text-primary">orbits</span> · related nodes circle their center — drag a
            ring node to change its angle, a center to move its whole system
          </div>
          {dirty && (
            <div className="rounded-full border border-primary/40 bg-popover/85 px-3 py-1.5 font-mono text-2xs text-primary backdrop-blur">
              connections changed — Recompute to re-tidy the orbits
            </div>
          )}
        </div>
        <div className="pointer-events-auto flex items-center gap-2">
          <Tip label="Switch to the atlas (the full force-graph)">
            <Button size="sm" variant="outline" onClick={() => useTabs.getState().activate("graph")}>
              <Waypoints className="h-3.5 w-3.5 text-primary/80" />
              Atlas
            </Button>
          </Tip>
          {pinTarget && (
            <Tip label={`Pin the "${pinTarget.label}" orbit so it's close at hand`}>
              <Button size="sm" variant="secondary" onClick={() => void pinOrbit()} disabled={pinning}>
                {pinning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Pin className="h-3.5 w-3.5" />}
                Pin orbit
              </Button>
            </Tip>
          )}
          <Button
            size="sm"
            variant={dirty ? "default" : "outline"}
            onClick={onRecompute}
            disabled={recompute.isPending}
          >
            {recompute.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" />
            )}
            Recompute Orbits
          </Button>
          <Button size="sm" variant="secondary" onClick={() => void createOrbit()} disabled={busy}>
            {busy ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Plus className="h-3.5 w-3.5" />
            )}
            New orbit
          </Button>
        </div>
      </div>

      {/* Loading / error / empty states */}
      {orbits.isLoading && (
        <div className="absolute inset-0 grid place-items-center">
          <LoadingState label="charting orbits…" />
        </div>
      )}
      {orbits.isError && (
        <div className="absolute inset-0 grid place-items-center">
          <ErrorState error={orbits.error} onRetry={() => void orbits.refetch()} label="orbits" />
        </div>
      )}
      {isEmpty && (
        // pointer-events-none on the full-canvas wrapper so an empty orbit
        // view still passes double-clicks through to the canvas (quick-add);
        // only the actual button re-enables pointer events.
        <div className="pointer-events-none absolute inset-0 grid place-items-center">
          <div className="[&_button]:pointer-events-auto">
            <EmptyState
              icon={OrbitIcon}
              title="No orbits yet"
              description="Orbits arrange your graph by association: each hub becomes a center and the nodes connected to it circle around it. Press Recompute to build them from your connections."
              action={{ label: "Recompute Orbits", onClick: onRecompute }}
            />
          </div>
        </div>
      )}

      <QuickAddMenu pos={quickAdd} onClose={() => setQuickAdd(null)} onAddWebsite={onAddWebsite} />
    </div>
  );
}
